import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Paper,
  IconButton,
  InputAdornment
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';

const dialogTitleSx = {
  textAlign: 'center',
  pb: 0
};

const headerLineSx = { fontWeight: 700, fontSize: 18 };
const headerSubSx = { fontWeight: 600, fontSize: 16, mt: 0.5 };
const headerSmallSx = { fontWeight: 600, fontSize: 15, mt: 0.5 };

const listPaperSx = {
  width: 340,
  height: 360,
  overflow: 'auto',
  borderRadius: 1,
  border: '1px solid #cfd8dc'
};

const FarmEventDialog = ({
  open = true,
  onClose = () => {},
  onSave = () => {},
  onMove = () => {},
  selectedFarmEvent, 
  fieldName,
  farmEvents
}) => {
  // Header values (mocked from selectedFarmEvent where available)
  const dateRange = selectedFarmEvent?.dateRange;
  const totalGallons = selectedFarmEvent?.totalSprayedGallons ?? 0;

  // Name field
  const [name, setName] = useState(selectedFarmEvent?.name ?? '');

  useEffect(() => {
    setName(selectedFarmEvent?.name ?? '');
  }, [selectedFarmEvent]);

  // Build includedRuns from selectedFarmEvent.subRuns
  const includedRuns = useMemo(() => {
    if (!selectedFarmEvent?.subRuns || !Array.isArray(selectedFarmEvent.subRuns)) return [];
    return selectedFarmEvent.subRuns.map(subRun => {
      const sprayer = subRun.tractorName || 'Sprayer';
      const date = subRun.startTimeTractorTimezone
        ? new Date(subRun.startTimeTractorTimezone).toLocaleDateString()
        : '';
      const gallons = subRun.totalPesticideSprayedGallons ?? 0;
      return `${sprayer} - ${date} - ${gallons.toFixed(1)} Gal`;
    });
  }, [selectedFarmEvent?.subRuns]);

  const [includedSelectedIndex, setIncludedSelectedIndex] = useState(-1);

  const filteredEvents = useMemo(() => {
    if (!Array.isArray(farmEvents) || !selectedFarmEvent) return [];
    return farmEvents
      .filter(evt => evt.id !== selectedFarmEvent.id)
      .map(evt => evt.name);
  }, [farmEvents, selectedFarmEvent]);

  const [events, setEvents] = useState(filteredEvents);

  const [eventsSelectedIndex, setEventsSelectedIndex] = useState(-1);

  useEffect(() => {
    setEvents(filteredEvents);
  }, [filteredEvents]);

  // Add this state to track the actual subRuns
  const [includedSubRunsState, setIncludedSubRunsState] = useState(selectedFarmEvent?.subRuns ?? []);

  // Keep display strings in sync
  const includedRunsState = useMemo(() => {
    return includedSubRunsState.map(subRun => {
      const sprayer = subRun.tractorName || 'Sprayer';
      const date = subRun.startTimeTractorTimezone
        ? new Date(subRun.startTimeTractorTimezone).toLocaleDateString()
        : '';
      const gallons = subRun.totalPesticideSprayedGallons ?? 0;
      return `${sprayer} - ${date} - ${gallons.toFixed(1)} Gal`;
    });
  }, [includedSubRunsState]);

  // Recalculate total gallons
  const totalGallonsMemo = useMemo(() => {
    return includedSubRunsState.reduce(
      (sum, subRun) => sum + (subRun.totalPesticideSprayedGallons ?? 0),
      0
    );
  }, [includedSubRunsState]);

  useEffect(() => {
    setIncludedSubRunsState(selectedFarmEvent?.subRuns ?? []);
  }, [selectedFarmEvent]);

  // Move from Events -> Included Runs
  const handleMoveRightToLeft = async () => {
    if (
      includedSelectedIndex < 0 ||
      eventsSelectedIndex < 0 ||
      !selectedFarmEvent ||
      !farmEvents?.length
    ) return;

    const subRun = includedSubRunsState[includedSelectedIndex];
    if (!subRun) return;

    const filteredEventNames = farmEvents
      .filter(evt => evt.id !== selectedFarmEvent.id)
      .map(evt => evt.name);

    const destinationEventName = filteredEventNames[eventsSelectedIndex];
    const destinationEvent = farmEvents.find(evt => evt.name === destinationEventName);

    if (!destinationEvent) return;

    const moveRequest = {
      subRunId: subRun.id,
      toFarmEventId: destinationEvent.id
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/farmEvents/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(moveRequest)
      });

      if (!response.ok) throw new Error('Move failed');

      // Remove from includedSubRunsState
      const newIncludedSubRuns = includedSubRunsState.filter((_, idx) => idx !== includedSelectedIndex);
      setIncludedSubRunsState(newIncludedSubRuns);
      setIncludedSelectedIndex(-1);

      toast.success(`Successfully moved to ${destinationEvent.name}`);

      // Trigger refresh in BlockDetails.js
      if (typeof onMove === 'function') {
        onMove();
      }
    } catch (err) {
      toast.error('Move failed');
    }
  };

  // Save handler for icon button
  const handleSaveName = async () => {
    const updatedEvent = {
      ...selectedFarmEvent,
      name,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/farmEvents`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedEvent),
      });

      if (!response.ok) {
        toast.error('Failed to update farm event');
        return;
      }

      onSave(updatedEvent);
      onClose();
      toast.success('Event name updated!');
    } catch (error) {
      toast.error('Error updating farm event');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle sx={{ ...dialogTitleSx, position: 'relative' }}>
        <Typography sx={headerLineSx}>Edit Event - {name}</Typography>
        <Typography sx={headerLineSx}>{fieldName}</Typography>
        <Typography sx={headerSubSx}>{dateRange}</Typography>
        <Typography sx={headerSmallSx}>{totalGallonsMemo.toFixed(1)} Total Gallons</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Name */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Name
          </Typography>
          <TextField
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Event name"
            sx={{ width: 340 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Save name"
                    color="primary"
                    onClick={handleSaveName}
                    edge="end"
                  >
                    <SaveIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Stack direction="row" spacing={4} alignItems="center" justifyContent="center">
          {/* Included Runs (Left) */}
          <Box sx={{ minWidth: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Included Runs
            </Typography>
            <Paper variant="outlined" sx={listPaperSx}>
              <List dense disablePadding>
                {includedRunsState.map((text, idx) => (
                  <ListItemButton
                    key={`${text}-${idx}`}
                    selected={idx === includedSelectedIndex}
                    onClick={() => setIncludedSelectedIndex(idx)}
                  >
                    <ListItemText primary={text} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Box>

          {/* Move buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleMoveRightToLeft}
              sx={{ minWidth: 140 }}
              disabled={includedSelectedIndex < 0 || eventsSelectedIndex < 0}
            >
              Move -&gt;
            </Button>
          </Box>

          {/* Events (Right) */}
          <Box sx={{ minWidth: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Events
            </Typography>
            <Paper variant="outlined" sx={{ ...listPaperSx, width: 320 }}>
              <List dense disablePadding>
                {events.map((text, idx) => (
                  <ListItemButton
                    key={`${text}-${idx}`}
                    selected={idx === eventsSelectedIndex}
                    onClick={() => setEventsSelectedIndex(idx)}
                  >
                    <ListItemText primary={text} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default FarmEventDialog;
